/**
 * Logger Composable - Environment-Controlled Logging
 *
 * Provides consistent, tagged logging across the application with centralized control
 * via the NUXT_LOGS_ENABLED environment variable.
 *
 * @example
 * // In a store
 * const log = useLogger('store:commerce')
 * log.info('✅ Products loaded successfully')
 *
 * // In a composable
 * const log = useLogger('fetch:auth')
 * log.info('🔍 Performing authentication check')
 *
 * // In a component
 * const log = useLogger('component:cart')
 * log.info('🛒 Item added to cart')
 */

import { consola } from 'consola'

/**
 * Create a tagged logger instance with environment-controlled logging
 *
 * This avoids @nuxt/kit's useLogger which causes "#imports should be transformed" errors
 * when used in runtime code (components, composables, pages, plugins).
 *
 * @param tag - The tag to identify the source (e.g., 'store:commerce', 'fetch:auth', 'component:cart')
 * @param forceVisible - If provided, completely overrides all environment settings (true=force enable, false=force disable)
 * @returns Logger instance with info, warn, error methods
 */
export const useLogger = (tag: string, forceVisible?: boolean) => {
  // Get environment config safely without useRuntimeConfig to avoid auto-import issues
  const LOGS_ENABLED_FROM_ENV = typeof process !== 'undefined'
    ? process.env.NUXT_LOGS_ENABLED === 'true'
    : false

  // forceVisible completely overrides all environment settings when provided
  const LOGS_ENABLED = forceVisible !== undefined ? forceVisible : LOGS_ENABLED_FROM_ENV

  // Create consola instance with tag and environment-controlled level
  const logger = consola.withTag(tag).withDefaults({
    level: LOGS_ENABLED ? 4 : 0,
  })

  // Create no-op functions when logging is disabled
  const noop = () => {}

  return {
    /**
     * Log informational messages (level 3)
     * Only shows when NUXT_LOGS_ENABLED=true
     */
    info: LOGS_ENABLED ? Function.prototype.bind.call(logger.info, logger) : noop,

    /**
     * Log warning messages (level 2)
     * Always shows regardless of log settings
     */
    warn: Function.prototype.bind.call(logger.warn, logger),

    /**
     * Log error messages (level 1)
     * Always shows regardless of log settings
     */
    error: Function.prototype.bind.call(logger.error, logger),

    /**
     * Log debug messages (level 4)
     * Only shows when NUXT_LOGS_ENABLED=true
     */
    debug: LOGS_ENABLED ? Function.prototype.bind.call(logger.debug, logger) : noop,

    /**
     * Log success messages (level 3, with success styling)
     * Only shows when NUXT_LOGS_ENABLED=true
     */
    success: LOGS_ENABLED ? Function.prototype.bind.call(logger.success, logger) : noop,

    /**
     * Always log critical errors regardless of environment setting
     * Use sparingly for errors that must always be visible
     */
    fatal: Function.prototype.bind.call(consola.error, consola),

    /**
     * Access to the raw consola instance for advanced usage
     */
    raw: logger,
  }
}

/**
 * Type definition for the logger instance
 */
export type Logger = ReturnType<typeof useLogger>
